import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/axios";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function SchoolSignup() {
  const [step, setStep] = useState(1);
  const [schoolId, setSchoolId] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Step 1 form state
  const [form, setForm] = useState({
    schoolName: "",
    address: "",
    contactEmail: "",
    contactMobile: "",
    adminName: "",
    adminUsername: "",
    adminPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [step1Loading, setStep1Loading] = useState(false);

  // Step 2 state
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    if (step === 2) {
      api.get("/plans/public").then((r) => setPlans(r.data)).catch(() => {
        setError("Could not load plans. Please refresh.");
      });
    }
  }, [step]);

  async function handleStep1(e) {
    e.preventDefault();
    setError("");
    setStep1Loading(true);
    try {
      const { data } = await api.post("/auth/register-school", form);
      setSchoolId(data.schoolId);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || "Registration failed. Please try again.");
    } finally {
      setStep1Loading(false);
    }
  }

  async function handlePay(plan) {
    setSelectedPlan(plan);
    setError("");
    setPayLoading(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        setError("Failed to load payment gateway. Check your connection and try again.");
        setPayLoading(false);
        return;
      }

      const { data: order } = await api.post("/payments/create-order", {
        schoolId,
        planId: plan.id,
      });

      const options = {
        key: order.razorpayKeyId,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Volcanic Result Management System",
        description: `${plan.name} — ${plan.duration_days} days`,
        order_id: order.orderId,
        handler: async function (response) {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              schoolId,
              planId: plan.id,
            });
            setSuccess(true);
          } catch (verifyErr) {
            setError("Payment verification failed. Contact support with your payment ID: " + response.razorpay_payment_id);
          }
        },
        prefill: {
          name: form.adminName,
          email: form.contactEmail,
          contact: form.contactMobile,
        },
        theme: { color: "#2563EB" },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        setError("Payment failed: " + (response.error?.description || "Unknown error"));
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.error || "Could not initiate payment. Try again.");
    } finally {
      setPayLoading(false);
    }
  }

  if (success) {
    return (
      <div className="signup-page">
        <header className="signup-header">
          <div className="logo-on-dark" style={{ padding: "4px 6px", borderRadius: 8 }}>
            <img src="/logo.png" alt="VRM" className="logo-img-sm" />
          </div>
          <div>
            <div className="signup-header-title">Volcanic Result Management System</div>
            <div className="signup-header-unit">Volcanic Education and Technology Pvt Ltd</div>
          </div>
        </header>
        <div className="signup-body">
          <div className="signup-container">
            <div className="card" style={{ textAlign: "center", padding: "48px 32px" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
              <h2 style={{ color: "var(--success)", marginBottom: 8 }}>Payment Successful!</h2>
              <p style={{ fontSize: 16, color: "var(--muted)", marginBottom: 24 }}>
                Your school account is now <strong>active</strong>. You can log in immediately with the credentials you created.
              </p>
              <div style={{ background: "var(--success-bg)", border: "1px solid #bbf7d0", borderRadius: 8, padding: "14px 20px", marginBottom: 28, textAlign: "left" }}>
                <strong style={{ color: "var(--success)" }}>Your login details:</strong>
                <div style={{ marginTop: 8, fontSize: 14, color: "var(--text)" }}>
                  <div>Username: <code style={{ background: "#f0fdf4", padding: "1px 6px", borderRadius: 4 }}>{form.adminUsername}</code></div>
                  <div style={{ marginTop: 4 }}>Use the password you set during registration.</div>
                </div>
              </div>
              <Link to="/login" className="btn large" style={{ display: "inline-flex" }}>
                Go to Login →
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <header className="signup-header">
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <div className="logo-on-dark" style={{ padding: "4px 6px", borderRadius: 8 }}>
            <img src="/logo.png" alt="VRM" className="logo-img-sm" />
          </div>
          <div>
            <div className="signup-header-title">Volcanic Result Management System</div>
            <div className="signup-header-unit">Volcanic Education and Technology Pvt Ltd</div>
          </div>
        </Link>
      </header>

      <div className="signup-body">
        <div className="signup-container">
          {/* Step Indicator */}
          <div className="steps-indicator">
            <div className={`step-dot ${step === 1 ? "active" : step > 1 ? "done" : ""}`}>
              {step > 1 ? "✓" : "1"}
            </div>
            <span className={`step-label ${step === 1 ? "active" : step > 1 ? "done" : ""}`}>
              School Details
            </span>
            <div className={`step-connector ${step > 1 ? "done" : ""}`} />
            <div className={`step-dot ${step === 2 ? "active" : step > 2 ? "done" : ""}`}>
              {step > 2 ? "✓" : "2"}
            </div>
            <span className={`step-label ${step === 2 ? "active" : step > 2 ? "done" : ""}`}>
              Choose a Plan &amp; Pay
            </span>
          </div>

          {/* Step 1 */}
          {step === 1 && (
            <div className="card">
              <h2 style={{ marginBottom: 4 }}>Register Your School</h2>
              <p className="subtitle" style={{ marginBottom: 0 }}>
                Already have an account? <Link to="/login">Sign in here</Link>
              </p>
              <div className="divider" />

              {error && <div className="error-text" style={{ marginBottom: 16 }}>{error}</div>}

              <form onSubmit={handleStep1}>
                <div className="form-section-title">School Information</div>
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">School Name *</label>
                    <input
                      placeholder="e.g. St. Mary's High School"
                      value={form.schoolName}
                      onChange={(e) => setForm({ ...form, schoolName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Address</label>
                    <input
                      placeholder="School address"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Contact Email *</label>
                    <input
                      type="email"
                      placeholder="school@example.com"
                      value={form.contactEmail}
                      onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Contact Mobile</label>
                    <input
                      placeholder="10-digit mobile number"
                      value={form.contactMobile}
                      onChange={(e) => setForm({ ...form, contactMobile: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-section-title">Admin Account</div>
                <div className="form-grid">
                  <div className="form-field">
                    <label className="form-label">Admin Full Name *</label>
                    <input
                      placeholder="Principal / Admin name"
                      value={form.adminName}
                      onChange={(e) => setForm({ ...form, adminName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Admin Username *</label>
                    <input
                      placeholder="e.g. stmary_admin"
                      value={form.adminUsername}
                      onChange={(e) => setForm({ ...form, adminUsername: e.target.value })}
                      required
                      autoComplete="username"
                    />
                  </div>
                  <div className="form-field" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Admin Password *</label>
                    <div className="input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Minimum 8 characters"
                        value={form.adminPassword}
                        onChange={(e) => setForm({ ...form, adminPassword: e.target.value })}
                        required
                        minLength={8}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="input-eye-btn"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                      >
                        {showPassword ? "🙈" : "👁️"}
                      </button>
                    </div>
                  </div>
                </div>

                <button className="btn large" type="submit" disabled={step1Loading} style={{ width: "100%", marginTop: 8 }}>
                  {step1Loading ? "Creating account..." : "Continue to Payment →"}
                </button>
              </form>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div>
              <div className="card">
                <h2 style={{ marginBottom: 4 }}>Choose a Subscription Plan</h2>
                <p className="subtitle">Select a plan to activate your school account. Payment is processed securely via Razorpay.</p>

                {error && <div className="error-text" style={{ marginBottom: 16 }}>{error}</div>}

                {plans.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">⏳</div>
                    <div className="empty-state-text">Loading available plans...</div>
                  </div>
                ) : (
                  <div className="plan-select-grid">
                    {plans.map((plan) => (
                      <div
                        key={plan.id}
                        className={`plan-select-card${selectedPlan?.id === plan.id ? " selected" : ""}`}
                        onClick={() => setSelectedPlan(plan)}
                      >
                        <div style={{ fontSize: 24, marginBottom: 8 }}>📦</div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>{plan.name}</div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: "var(--blue)", letterSpacing: -1 }}>
                          ₹{Number(plan.price).toLocaleString("en-IN")}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>{plan.duration_days} days</div>
                        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px", textAlign: "left" }}>
                          <li style={{ fontSize: 13, color: "var(--muted)", padding: "4px 0" }}>✓ Unlimited students</li>
                          <li style={{ fontSize: 13, color: "var(--muted)", padding: "4px 0" }}>✓ PDF result cards</li>
                          <li style={{ fontSize: 13, color: "var(--muted)", padding: "4px 0" }}>✓ Excel export</li>
                          {plan.feature_flags?.graphs && (
                            <li style={{ fontSize: 13, color: "var(--muted)", padding: "4px 0" }}>✓ Performance graphs</li>
                          )}
                        </ul>
                        <button
                          className="btn"
                          style={{ width: "100%" }}
                          disabled={payLoading}
                          onClick={(e) => { e.stopPropagation(); handlePay(plan); }}
                        >
                          {payLoading && selectedPlan?.id === plan.id ? "Processing..." : "Pay & Activate"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ textAlign: "center", fontSize: 13, color: "var(--muted)", marginTop: 8 }}>
                <span>🔒 Payments secured by Razorpay. </span>
                <button
                  className="btn secondary sm"
                  style={{ marginLeft: 8 }}
                  onClick={() => { setStep(1); setError(""); }}
                >
                  Back to Details
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
